import { useState, useEffect, useCallback } from 'react';

const COOLDOWN_MS = 60000;
const LAST_ACTION_KEY = 'lastBlockchainActionTime';

export interface CooldownState {
  isOnCooldown: boolean;
  remainingSeconds: number;
  checkCooldown: () => boolean;
  startCooldown: () => void;
}

export function useBlockchainCooldown(): CooldownState {
  const [isOnCooldown, setIsOnCooldown] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  const checkCooldown = useCallback(() => {
    const lastAction = localStorage.getItem(LAST_ACTION_KEY);
    if (lastAction) {
      const lastTime = parseInt(lastAction, 10);
      const elapsed = Date.now() - lastTime;
      if (elapsed < COOLDOWN_MS) {
        const remaining = Math.ceil((COOLDOWN_MS - elapsed) / 1000);
        setRemainingSeconds(remaining);
        setIsOnCooldown(true);
        return false;
      }
    }
    setIsOnCooldown(false);
    setRemainingSeconds(0);
    return true;
  }, []);

  const startCooldown = useCallback(() => {
    localStorage.setItem(LAST_ACTION_KEY, Date.now().toString());
    setIsOnCooldown(true);
    setRemainingSeconds(Math.ceil(COOLDOWN_MS / 1000));
  }, []);

  useEffect(() => {
    if (!isOnCooldown) return;

    const interval = setInterval(() => {
      const lastAction = localStorage.getItem(LAST_ACTION_KEY);
      if (lastAction) {
        const lastTime = parseInt(lastAction, 10);
        const elapsed = Date.now() - lastTime;
        if (elapsed >= COOLDOWN_MS) {
          setIsOnCooldown(false);
          setRemainingSeconds(0);
          clearInterval(interval);
        } else {
          setRemainingSeconds(Math.ceil((COOLDOWN_MS - elapsed) / 1000));
        }
      } else {
        setIsOnCooldown(false);
        setRemainingSeconds(0);
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isOnCooldown]);

  return {
    isOnCooldown,
    remainingSeconds,
    checkCooldown,
    startCooldown,
  };
}
